import { GitCommit, PenBoxIcon, PauseIcon, RefreshCwIcon } from 'lucide-react';

import { Icon } from '@@/Icon';
import { ResourceDetailHeader } from '@@/ResourceDetailHeader/ResourceDetailHeader';
import { HeaderStats } from '@@/ResourceDetailHeader/HeaderStats';
import { ResourceStatBlock } from '@@/ResourceDetailHeader/ResourceStatBlock';
import { ActionBarShell } from '@@/ResourceDetailHeader/ActionBarShell';
import { Button } from '@@/buttons';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { LinkButton } from '@@/LinkButton';

import { Workflow } from '../types';
import { StatusBadge } from '../../components/StatusBadge';
import { effectiveWorkflowStatus, computeTargetRollup } from '../status';
import { getDeployedStackLink } from '../utils';

import { WorkflowDeleteButton } from './WorkflowDeleteButton';

const COMING_SOON_MESSAGE = 'Coming soon';

interface Props {
  workflow: Workflow;
}

export function WorkflowResourceHeader({ workflow }: Props) {
  const status = effectiveWorkflowStatus(workflow);
  const rollup = computeTargetRollup(workflow);
  const [primaryArtifact] = workflow.artifacts;
  const stackLink = primaryArtifact
    ? getDeployedStackLink(primaryArtifact)
    : null;

  return (
    <ResourceDetailHeader
      icon={<Icon icon={GitCommit} size="xl" />}
      title={workflow.name}
      badge={<StatusBadge status={status.status} />}
      rightInfo={
        <HeaderStats>
          <ResourceStatBlock
            status={rollup.tone}
            data-cy="workflow-targets-stat"
          >
            <ResourceStatBlock.Label>Targets</ResourceStatBlock.Label>
            <ResourceStatBlock.Value align="center" size="base" dot>
              {rollup.total > 0
                ? `${rollup.synced}/${rollup.total} synced`
                : 'No artifacts'}
            </ResourceStatBlock.Value>
          </ResourceStatBlock>
        </HeaderStats>
      }
      actionBar={
        <ActionBarShell>
          <div className="flex items-center gap-2">
            <LinkButton
              icon={PenBoxIcon}
              color="light"
              disabled={!stackLink}
              to={stackLink?.to ?? ''}
              params={stackLink?.params}
              data-cy="workflow-edit-button"
            >
              Edit
            </LinkButton>
            <TooltipWithChildren message={COMING_SOON_MESSAGE}>
              <span>
                <Button
                  icon={RefreshCwIcon}
                  color="light"
                  disabled
                  data-cy="workflow-sync-button"
                >
                  Force sync
                </Button>
              </span>
            </TooltipWithChildren>
            <TooltipWithChildren message={COMING_SOON_MESSAGE}>
              <span>
                <Button
                  icon={PauseIcon}
                  color="light"
                  disabled
                  data-cy="workflow-pause-button"
                >
                  Pause
                </Button>
              </span>
            </TooltipWithChildren>
          </div>
          <div className="ml-auto">
            <WorkflowDeleteButton workflow={workflow} />
          </div>
        </ActionBarShell>
      }
    />
  );
}
